// webapp/src/lib/offlinePrefetch.js
//
// Phase 2a — offline prefetch. Pre-warms Phase 1's read cache. Two halves:
//   - Feed plan (everyone): the frontpage feed + race news for the current
//     hostname, plus warming the service worker's image cache. See
//     docs/superpowers/specs/2026-08-09-offline-feed-design.md.
//   - Dyrejournal plan (gated): entity data for the dyrejournal-sale screen,
//     only with featureFlags.enableOfflinePrefetch AND the dyrejournal menu.
//     See docs/superpowers/specs/2026-05-06-offline-prefetch-design.md.

const LAST_AT_KEY = 'keepspot_lastPrefetchAt';
const IMAGE_WARM_CAP = 30;
const IMAGE_WARM_TIMEOUT_MS = 8000;

// ---------------------------------------------------------------------------
// Image warming — request through an <img> element so the fetch goes through
// the service worker's image StaleWhileRevalidate route exactly like a real
// render (a JS fetch() would need CORS on the image host; an image element
// caches fine as an opaque response).

function defaultWarmImage(url) {
    return new Promise((resolve) => {
        try {
            const img = new Image();
            const timer = setTimeout(() => resolve(false), IMAGE_WARM_TIMEOUT_MS);
            img.onload = () => { clearTimeout(timer); resolve(true); };
            img.onerror = () => { clearTimeout(timer); resolve(false); };
            img.src = url;
        } catch (e) {
            resolve(false);
        }
    });
}

// ---------------------------------------------------------------------------
// Lazy default deps — production code uses these. Tests inject overrides via
// _setDependenciesForTesting().

let _deps = null;

function defaultDeps() {
    if (_deps) return _deps;
    // Lazy-required to avoid circular imports at module-eval time.
    const util = require('preact-util').default;
    const PubSub = require('./pubsub').default;
    const appState = require('../stores/appState').default;
    const userStore = require('../stores/user').default;
    _deps = {
        fetchApi: (url, opts, body) => util.fetchApi(url, opts, body),
        pubsub: PubSub,
        appState,
        userStore,
        warmImage: defaultWarmImage,
    };
    return _deps;
}

export function _setDependenciesForTesting(deps) {
    _deps = deps;
}

export function _resetForTesting() {
    _deps = null;
    _inFlight = null;
}

// ---------------------------------------------------------------------------
// Plan builders

function buildFeedPlan(hostname) {
    // Bodies mirror ArticleList.loadAll / fetchAndMergeNews byte-for-byte so
    // the frontpage's own requests get exact-key cache hits; the feed
    // fallback (lib/offlineFeedFallback.js) covers other limit/offset values.
    return [
        {
            label: 'Nyhetsstrøm',
            url: '/api/articles/public/',
            isFeed: true,
            body: {
                hostname,
                category: 'feed',
                limit: 25,
                offset: 0,
                sort: '-sort -createdDate',
                addData: 'creators,likes,comments,tags,polls',
            },
        },
        {
            label: 'Nyheter',
            url: '/api/news/public/',
            isFeed: true,
            body: {
                hasRace: 1,
                hasConvention: 1,
                limit: 25,
                offset: 0,
                sort: '-publishDate',
                addData: 'races,conventions,creators,likes,comments',
            },
        },
    ];
}

function buildDyrejournalPlan(customerId) {
    return [
        { label: 'Besøkende',  url: '/api/visitors/',  body: { customer: customerId, sort: '-updatedDate', limit: 2000 } },
        { label: 'Dyr',        url: '/api/animals/',   body: { customer: customerId, sort: '-updatedDate', limit: 2000 } },
        { label: 'Produkter',  url: '/api/products/',  body: { customer: customerId, limit: 1000 } },
        { label: 'Medisiner',  url: '/api/drugs/',     body: { limit: 2000 } },
        { label: 'Diagnoser',  url: '/api/diagnoses/', body: { limit: 2000 } },
    ];
}

// The frontpage feed renders 1024x (ImageScroller via localUtil.displayImg
// '1024x' => s3XLargeLink); side layouts use 800x (s3LargeLink).
export function collectFeedImageUrls(responses, cap = IMAGE_WARM_CAP) {
    const urls = [];
    const seen = new Set();
    responses.forEach((response) => {
        const items = response && Array.isArray(response.data) ? response.data : [];
        items.forEach((item) => {
            const img = item && Array.isArray(item.images) ? item.images[0] : null;
            if (!img) return;
            const url = img.s3XLargeLink || img.s3LargeLink || img.s3MediumLink;
            if (!url || typeof url !== 'string' || seen.has(url)) return;
            seen.add(url);
            urls.push(url);
        });
    });
    return urls.slice(0, cap);
}

// ---------------------------------------------------------------------------
// Network-error envelope detection — matches Phase 1's wrapper. We don't
// import the helper from offlineCache.js because that's a slightly heavier
// module (with IDB plumbing); the check is two lines and worth duplicating
// to keep this module light.

function isNetworkErrorEnvelope(response) {
    if (!response || typeof response !== 'object') return false;
    if (response.status !== 500) return false;
    return typeof response.message === 'string'
        && response.message.indexOf('ERROR fetching API:') === 0;
}

// ---------------------------------------------------------------------------
// Public API

let _inFlight = null;

// NOTE: NOT declared `async`. An `async function` wraps its return value in
// a fresh outer Promise on every call, so two concurrent callers would see
// different promise references — failing the `p1 === p2` idempotency
// invariant. Making the outer function synchronous and returning the literal
// `_inFlight` Promise lets concurrent callers share one promise instance.
export function runPrefetch({ silent = false } = {}) {
    if (_inFlight) return _inFlight;

    _inFlight = (async () => {
        const deps = _deps || defaultDeps();
        const { fetchApi, pubsub, appState, userStore, warmImage } = deps;

        const currentCustomer = (appState && appState.currentCustomer) || {};
        const opts = (appState && appState.opts) || {};
        const customerId = currentCustomer.id != null ? currentCustomer.id : null;
        const featureFlags = (userStore && userStore.customer && userStore.customer.featureFlags) || {};
        const dyrejournalEnabled = !!(featureFlags.enableOfflinePrefetch
            && (currentCustomer.menuItems?.dyrejournal || opts.showDyrejournal)
            && customerId != null);

        const hostname = typeof window !== 'undefined' && window.location ? window.location.hostname : '';
        const plan = [...buildFeedPlan(hostname)];
        if (dyrejournalEnabled) {
            plan.push(...buildDyrejournalPlan(customerId));
        }

        const succeeded = [];
        const failed = [];
        const feedResponses = [];

        // +1 = the trailing image-warming step.
        const total = plan.length + 1;
        pubsub.publish('offlinePrefetchStarted', { total, silent });

        for (let i = 0; i < plan.length; i++) {
            const step = plan[i];
            pubsub.publish('offlinePrefetchProgress', { index: i, total, label: step.label });

            try {
                const response = await fetchApi(step.url, { method: 'GET' }, step.body);
                if (isNetworkErrorEnvelope(response)) {
                    failed.push({ step, error: { type: 'network', message: response.message } });
                } else {
                    succeeded.push({ step });
                    if (step.isFeed && response && response.status === 200) {
                        feedResponses.push(response);
                    }
                }
            } catch (err) {
                failed.push({ step, error: { type: 'thrown', message: (err && err.message) || String(err) } });
            }
        }

        // Image warming — best-effort; failures never count against the run.
        pubsub.publish('offlinePrefetchProgress', { index: plan.length, total, label: 'Bilder' });
        try {
            const imageUrls = collectFeedImageUrls(feedResponses);
            await Promise.all(imageUrls.map(url => Promise.resolve().then(() => warmImage(url)).catch(() => false)));
        } catch (e) {
            // ignore — image warming is opportunistic
        }

        try {
            localStorage.setItem(LAST_AT_KEY, String(Date.now()));
        } catch (e) {
            // localStorage may be unavailable (private mode quota etc.) — ignore.
        }

        pubsub.publish('offlinePrefetchComplete', { succeeded, failed, silent });

        return { succeeded, failed };
    })().finally(() => {
        _inFlight = null;
    });

    return _inFlight;
}

export function lastPrefetchAt() {
    try {
        const raw = localStorage.getItem(LAST_AT_KEY);
        if (!raw) return null;
        const n = parseInt(raw, 10);
        return Number.isFinite(n) ? n : null;
    } catch (e) {
        return null;
    }
}

export function getInFlight() {
    return _inFlight;
}
